import logEvent from 'analytics/logEvent';
import cn from 'classnames';
import useTranslationAndTypograph from 'hooks/useTranslationAndTypograph';
import useValidation from 'hooks/useValidation';
import Cookies from 'js-cookie';
import type { FormEvent } from 'react';
import React, { useMemo, useState } from 'react';
import * as yup from 'yup';
import type { SchemaOf } from 'yup';

import pageUrls from 'constants/pagesUrls';

import ALink from 'components/basic/ALink';
import Button from 'components/basic/Button';
import ErrorLabel from 'components/basic/ErrorLabel';
import InputText from 'components/basic/InputText';
import { BodyM, Caption } from 'components/basic/Typography';
import SocialLogins from 'components/complex/SocialLogins';

import { useAppDispatch, useAppSelector } from 'redux-store';
import type { AuthorizationRequestType } from 'redux-store/slices/profile/types';
import authorizationThunk from 'redux-store/thunks/profile/authorizationThunk';

import s from './AuthForm.module.scss';

export default function AuthForm({
  redirectUrl = '',
  onLogin,
}: {
  redirectUrl?: string;
  onLogin?: () => void;
}): JSX.Element {
  const authError = useAppSelector(appState => {
    return appState.profile.error;
  });
  const providers = useAppSelector(state => state.profile.providers);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslationAndTypograph();
  const schema = useMemo((): SchemaOf<AuthorizationRequestType> => {
    return yup.object().shape({
      email: yup.string().email('Invalid email format').min(1).required('Please enter your email'),
      password: yup.string().min(1).required('Please create a password'),
    });
  }, []);
  const { validationErrors, validateAllFields, doValidate } =
    useValidation<AuthorizationRequestType>({ email, password }, schema);
  const dispatch = useAppDispatch();

  function buttonClickLogEvent(method: string): void {
    logEvent('Web_Auth_Sign-in_Button_Clicked', {
      method: method,
    });
  }

  async function doLogin(e: FormEvent): Promise<void> {
    e.preventDefault();

    const isValidationPassed = await validateAllFields();

    buttonClickLogEvent('email');

    if (isValidationPassed) {
      setIsLoading(true);

      Cookies.set('next-auth-redirect-url', redirectUrl);

      await dispatch(
        authorizationThunk({
          email,
          password,
        }),
      );

      onLogin?.();

      setIsLoading(false);
    }
  }

  return (
    <>
      <form onSubmit={doLogin}>
        <InputText<AuthorizationRequestType>
          labelText={t('Email')}
          value={email}
          name="email"
          onChange={setEmail}
          doValidate={doValidate}
          errorMessage={validationErrors.email}
          autocomplete="email"
          className="mb-2 cy-login-email-input"
        />
        <InputText<AuthorizationRequestType>
          labelText={t('Password')}
          value={password}
          name="password"
          type="password"
          autocomplete="current-password"
          onChange={setPassword}
          doValidate={doValidate}
          errorMessage={validationErrors.password}
          className="mb-2 cy-login-password-input"
        />
        <Caption className={cn('mb-5', s.resetPassword)}>
          <ALink
            className={cn(s.resetPasswordLink, "cy-reset-password-button")}
            onClick={(): void => {
              logEvent('Web_Auth_Sign-in_Reset_Link_Clicked');
            }}
            href={pageUrls.resetPassword}
          >
            {t('Reset password')}
          </ALink>
        </Caption>
        {authError && <ErrorLabel className="mb-4">{t(authError)}</ErrorLabel>}
        <Button
          type="submit"
          disabled={isLoading}
          isLoading={isLoading}
          className={cn(s.button, 'mb-3 cy-login-button')}
          size="medium"
        >
          {t('Log in')}
        </Button>
      </form>
      <Caption className="mb-3">{t('or continue with')}</Caption>
      <SocialLogins
        redirectUrl={redirectUrl}
        providers={providers}
        showProviderName={false}
        buttonClickLogEvent={buttonClickLogEvent}
      />
    </>
  );
}
